import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { getLocaleDate } from '@shared/utils/appUtils';
import * as PropTypes from 'prop-types';
import { mergeStyleSets, Text, Persona, PersonaSize, Stack } from '@fluentui/react';
import { NeutralColors } from '@fluentui/theme';
import { AppReviewItemCommentComposer } from '@shared/components/appReviewItemCommentComposer';
import { Constants } from '@shared/utils/constants';
import { launchTelemetry } from '@shared/utils/reviewsUtils';
import { logger } from '@src/logger';
import { AppReviewCommentItemToolbar } from '@shared/components/appReviewCommentItemToolbar';
const appReviewCommentItemStyles = mergeStyleSets({
    commentItem: {
        marginTop: '24px'
    },
    commentAuthorName: {
        color: NeutralColors.gray130,
        fontWeight: 600
    },
    deletedItemText: {
        color: '#4f4f4f',
        fontStyle: 'italic'
    },
    commentAuthorInfoBar: {
        marginBottom: '10px',
        minHeight: '35px'
    },
    editedLabel: {
        color: NeutralColors.gray120
    },
    postTime: {
        color: NeutralColors.gray130,
        overflow: 'hidden'
    },
    smallScreenToolbar: [
        'ms-hiddenLgUp',
        {
            marginTop: 10,
            marginBottom: 10
        }
    ],
    mediumScreenToolbar: [
        'ms-hiddenMdDown',
        {
            margin: '0 4px'
        }
    ],
    personaDetails: {
        paddingRight: 0
    }
});
const commentAuthorInfoBarStackTokenDef = {
    childrenGap: 4
};
export function AppReviewCommentItemContent({ comment }) {
    return /*#__PURE__*/ _jsx(Text, {
        children: comment.content
    });
}
export function AppReviewCommentItemEditedLabel({ context }) {
    return /*#__PURE__*/ _jsx(Text, {
        className: appReviewCommentItemStyles.editedLabel,
        children: context.loc('ReviewItem_CommentSection_Comment_Updated_Label', 'Edited')
    });
}
AppReviewCommentItemEditedLabel.contextTypes = {
    loc: PropTypes.func
};
export function AppReviewCommentItemPostTime({ comment, locale }, context) {
    return /*#__PURE__*/ _jsxs("div", {
        className: appReviewCommentItemStyles.postTime,
        children: [
            /*#__PURE__*/ _jsx(Text, {
                nowrap: true,
                block: true,
                className: "ms-hiddenMdDown",
                children: context.locParams('ReviewItem_CommentSection_Comment_Posted_Time_Label_Long', [
                    getLocaleDate(comment.updatedAt, locale)
                ], 'replied on {0}')
            }),
            /*#__PURE__*/ _jsx(Text, {
                nowrap: true,
                block: true,
                className: "ms-hiddenLgUp",
                children: context.locParams('ReviewItem_CommentSection_Comment_Posted_Time_Label_Short', [
                    getLocaleDate(comment.updatedAt, locale)
                ], '- {0}')
            })
        ]
    });
}
AppReviewCommentItemPostTime.contextTypes = {
    locParams: PropTypes.func
};
export function AppReviewCommentItemAuthorName({ comment }) {
    var _comment_customerInfo;
    return /*#__PURE__*/ _jsx(Persona, {
        onRenderPrimaryText: ()=>{
            var _comment_customerInfo;
            return /*#__PURE__*/ _jsx(Text, {
                className: appReviewCommentItemStyles.commentAuthorName,
                children: (_comment_customerInfo = comment.customerInfo) === null || _comment_customerInfo === void 0 ? void 0 : _comment_customerInfo.name
            });
        },
        text: (_comment_customerInfo = comment.customerInfo) === null || _comment_customerInfo === void 0 ? void 0 : _comment_customerInfo.name,
        size: PersonaSize.size24,
        styles: {
            details: appReviewCommentItemStyles.personaDetails
        }
    });
}
export const AppReviewCommentItem = ({ comment, locale, onDelete, onEdit }, context)=>{
    const [isEditing, setIsEditing] = useState(false);
    const [updatedContent, setUpdatedContent] = useState(comment.content);
    const toggleEdit = ()=>{
        setIsEditing((prevState)=>!prevState);
    };
    const onDeleteClick = ()=>{
        launchTelemetry(Constants.Telemetry.Action.Click, Constants.Telemetry.ActionModifier.ReviewItemCommentDeleteButton, {
            commentId: comment.id,
            reviewId: comment.reviewId
        });
        logger.info(JSON.stringify({
            commentId: comment.id,
            reviewId: comment.reviewId
        }), {
            action: Constants.Telemetry.Action.Click,
            actionModifier: Constants.Telemetry.ActionModifier.ReviewItemCommentDeleteButton
        });
        onDelete();
    };
    const onEditClick = ()=>{
        launchTelemetry(Constants.Telemetry.Action.Click, Constants.Telemetry.ActionModifier.ReviewItemCommentEditButton, {
            commentId: comment === null || comment === void 0 ? void 0 : comment.id,
            reviewId: comment === null || comment === void 0 ? void 0 : comment.reviewId
        });
        logger.info(JSON.stringify({
            commentId: comment === null || comment === void 0 ? void 0 : comment.id,
            reviewId: comment === null || comment === void 0 ? void 0 : comment.reviewId
        }), {
            action: Constants.Telemetry.Action.Click,
            actionModifier: Constants.Telemetry.ActionModifier.ReviewItemCommentEditButton
        });
        toggleEdit();
    };
    useEffect(()=>{
        setIsEditing(false);
    }, [
        comment
    ]);
    return /*#__PURE__*/ _jsx("div", {
        className: appReviewCommentItemStyles.commentItem,
        tabIndex: 0,
        children: !(comment === null || comment === void 0 ? void 0 : comment.isDeleted) ? /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs(Stack, {
                    tokens: commentAuthorInfoBarStackTokenDef,
                    horizontal: true,
                    verticalAlign: "center",
                    className: appReviewCommentItemStyles.commentAuthorInfoBar,
                    children: [
                        /*#__PURE__*/ _jsx(Stack.Item, {
                            disableShrink: true,
                            children: /*#__PURE__*/ _jsx(AppReviewCommentItemAuthorName, {
                                comment: comment
                            })
                        }),
                        /*#__PURE__*/ _jsx(AppReviewCommentItemPostTime, {
                            comment: comment,
                            locale: locale
                        }),
                        comment.isOwner && /*#__PURE__*/ _jsx(AppReviewCommentItemToolbar, {
                            className: appReviewCommentItemStyles.mediumScreenToolbar,
                            onDeleteToggle: onDeleteClick,
                            onEditToggle: onEditClick,
                            isEditing: isEditing
                        }),
                        comment.createdAt !== comment.updatedAt && /*#__PURE__*/ _jsx(AppReviewCommentItemEditedLabel, {
                            context: context
                        })
                    ]
                }),
                !isEditing && /*#__PURE__*/ _jsx(AppReviewCommentItemContent, {
                    comment: comment
                }),
                comment.isOwner && !isEditing && /*#__PURE__*/ _jsx(AppReviewCommentItemToolbar, {
                    className: appReviewCommentItemStyles.smallScreenToolbar,
                    onDeleteToggle: onDeleteClick,
                    onEditToggle: onEditClick,
                    isEditing: isEditing
                }),
                isEditing && /*#__PURE__*/ _jsx(AppReviewItemCommentComposer, {
                    initialContent: updatedContent,
                    editMode: true,
                    onCloseEdit: toggleEdit,
                    onChange: (content)=>setUpdatedContent(content),
                    onSubmit: ()=>onEdit(updatedContent),
                    onDeleteToggle: onDeleteClick,
                    onEditToggle: onEditClick
                })
            ]
        }) : /*#__PURE__*/ _jsx("div", {
            children: /*#__PURE__*/ _jsx(Stack, {
                horizontal: true,
                verticalAlign: "center",
                tokens: {
                    childrenGap: 8
                },
                children: /*#__PURE__*/ _jsx(Persona, {
                    size: PersonaSize.size24,
                    initialsColor: "#919191",
                    onRenderPrimaryText: ()=>/*#__PURE__*/ _jsx(Text, {
                            className: appReviewCommentItemStyles.deletedItemText,
                            children: context.loc('ReviewItem_CommentSection_Comment_Deleted_Label', 'This comment was deleted.')
                        }),
                    styles: {
                        details: appReviewCommentItemStyles.personaDetails
                    }
                })
            })
        })
    });
};
AppReviewCommentItem.contextTypes = {
    locParams: PropTypes.func,
    loc: PropTypes.func
};
